import * as React from "react";
import { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardHeader,
  Text,
  Divider,
  Input,
  Label,
  makeStyles,
  tokens,
  Badge,
  Spinner,
  CardPreview,
  Tooltip,
  MessageBar,
  MessageBarBody,
} from "@fluentui/react-components";
import { 
  DocumentText24Regular, 
  FolderAdd24Regular, 
  ArrowSync24Regular,
  CheckmarkCircle24Regular,
  DocumentSearch24Regular,
  DismissCircle24Regular,
} from "@fluentui/react-icons";
import { analyzeEmail, saveReference, validateReference } from "../services/api";

/* global Office */

interface EmailClassificationProps {
  insertText: (text: string) => void;
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    padding: "16px",
    gap: "16px",
    width: "100%",
    maxWidth: "600px",
    margin: "0 auto",
  },
  button: {
    marginTop: "16px",
  },
  securityCodeInput: {
    marginTop: "12px",
    marginBottom: "8px",
  },
  card: {
    marginTop: "16px",
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  folderSuggestion: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 0",
    borderRadius: tokens.borderRadiusMedium,
    transition: "all 0.1s ease",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  badge: {
    marginLeft: "8px",
  },
  referenceInput: {
    marginTop: "16px",
  },
  spinner: {
    margin: "24px auto",
  },
  subject: {
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: "8px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  folderIcon: {
    marginRight: "8px",
  },
  aiButton: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
});

const EmailClassification: React.FC<EmailClassificationProps> = (props: EmailClassificationProps) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [folderReference, setFolderReference] = useState("");
  const [referenceFound, setReferenceFound] = useState(false);
  const [detectedReference, setDetectedReference] = useState("");
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [suggestedFolders, setSuggestedFolders] = useState<Array<{name: string, confidence: number, reference: string}>>([]);
  const [emailId, setEmailId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [referenceError, setReferenceError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [securityCode, setSecurityCode] = useState("");
  const [securityCodeError, setSecurityCodeError] = useState("");
  
  // Get the email subject and ID from the selected email
  useEffect(() => {
    Office.context.mailbox.getCallbackTokenAsync({ isRest: true }, function (result) {
  if (result.status === Office.AsyncResultStatus.Succeeded) {
    const token = result.value;
    const ewsId = Office.context.mailbox.item.itemId;
    console.log("Token:", token);
    console.log("ItemId:", ewsId);
  }
});
    if (Office && Office.context && Office.context.mailbox && Office.context.mailbox.item) {
      setEmailSubject(Office.context.mailbox.item.subject || "No subject");
      // In a real implementation, you would get the actual email ID
      // For now, we'll use a simulated ID based on the current timestamp
      
      setEmailId(Office.context.mailbox.item.internetMessageId || "");
      console.log("email id is")
      console.log(Office.context.mailbox.item.internetMessageId);
    }
    else{
      console.log("email id not accesible")
      console.log()
    }
  }, []);

  const handleAnalysis = async () => {
    setIsAnalyzing(true);
    setErrorMessage("");
    
    try {
      // Call the real API
      const result = await analyzeEmail(emailId);
      
      // Update state with the response
      setReferenceFound(result.referenceFound);
      if (result.referenceFound && result.detectedReference) {
        setDetectedReference(result.detectedReference);
        // Auto-set the folder reference when a direct reference is found
        setFolderReference(result.detectedReference);
      }
      
      // Set suggested folders if any
      if (result.suggestedFolders && result.suggestedFolders.length > 0) {
        setSuggestedFolders(result.suggestedFolders);
      } else {
        // If direct reference was found but no suggested folders, create a default folder suggestion
        if (result.referenceFound && result.detectedReference) {
          setSuggestedFolders([{
            name: `Folder ${result.detectedReference}`,
            confidence: 1.0, // 100% confidence since it was directly detected
            reference: result.detectedReference
          }]);
        }
      }
      
      setShowResults(true);
    } catch (error) {
      console.error("Error analyzing email:", error);
      // Provide more detailed error message
      let errorMsg = "Failed to analyze email. ";
      
      if (error instanceof Error) {
        // If it's a standard error object, include its message
        errorMsg += error.message;
      } else {
        // Generic fallback message
        errorMsg += "Please try again or check if the API server is running.";
      }
      
      setErrorMessage(errorMsg);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleReferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFolderReference(value);
    
    // Clear any previous error when the user types
    if (referenceError) {
      setReferenceError("");
    }
    
    // Clear success message when user starts typing again
    if (saveSuccess) {
      setSaveSuccess(false);
    }
  };
  
  const handleSecurityCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Only allow numeric input and limit to 4 digits
    if (/^\d{0,4}$/.test(value)) {
      setSecurityCode(value);
      
      // Clear any previous error when the user types
      if (securityCodeError) {
        setSecurityCodeError("");
      }
    }
  };
  
  const validateSecurityCode = (): boolean => {
    // Check if security code is exactly 4 digits
    if (!/^\d{4}$/.test(securityCode)) {
      setSecurityCodeError("Please enter a valid 4-digit security code");
      return false;
    }
    return true;
  };

  const handleSaveReference = async () => {
    // Validate the reference format
    if (!validateReference(folderReference)) {
      setReferenceError("Invalid reference format. Please use format DT-XXXX-XX");
      return;
    }
    
    // Validate security code
    if (!validateSecurityCode()) {
      return;
    }
    
    try {
      // Call the API to save the reference and move the email
      const result = await saveReference(emailId, folderReference, securityCode);
      
      if (result.success) {
        // Show success message
        setSaveSuccess(true);
        // Insert the reference into the email
        props.insertText(`Folder Reference: ${folderReference} - Email moved successfully`);
        // Clear security code after successful operation
        setSecurityCode("");
      } else {
        // Show error message from API
        setReferenceError(result.message);
      }
    } catch (error) {
      console.error("Error saving reference:", error);
      setReferenceError("Failed to save reference. Please try again.");
    }
  };

  const styles = useStyles();

  return (
    <div className={styles.container}>
      <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", marginBottom: "16px" }}>
        <Button 
          appearance="primary" 
          size="large" 
          onClick={handleAnalysis}
          disabled={isAnalyzing}
          className={styles.aiButton}
        >
          AI Analysis
        </Button>
      </div>

      {/* Security code input - always visible */}
      <div className={styles.referenceInput} style={{ marginBottom: "16px" }}>
        <Label htmlFor="securityCode">Enter 4-digit security code</Label>
        <Input 
          id="securityCode"
          value={securityCode} 
          onChange={handleSecurityCodeChange} 
          placeholder="Enter 4-digit code"
          type="password"
        />
        {securityCodeError && (
          <Text style={{ color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200, marginTop: "4px" }}>
            {securityCodeError}
          </Text>
        )}
      </div>

      {/* Folder reference input - always visible */}
      <div className={styles.referenceInput} style={{ marginBottom: "16px" }}>
        <Label htmlFor="folderReference">Add folder reference (format: DT-XXXX-XX)</Label>
        <Input 
          id="folderReference"
          value={folderReference} 
          onChange={handleReferenceChange} 
          placeholder="Example: DT-2023-01"
        />
        {referenceError && (
          <Text style={{ color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200, marginTop: "4px" }}>
            {referenceError}
          </Text>
        )}
        
        {saveSuccess && (
          <Text style={{ color: "#0A75E1", fontSize: tokens.fontSizeBase200, marginTop: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
            <CheckmarkCircle24Regular />
            Reference saved successfully
          </Text>
        )}
        <Button 
          appearance="primary" 
          onClick={handleSaveReference} 
          disabled={!folderReference}
          className={styles.button}
        >
          Save Reference
        </Button>
      </div>

      {errorMessage && (
        <MessageBar intent="error">
          <MessageBarBody>
            {errorMessage}
          </MessageBarBody>
        </MessageBar>
      )}

      {isAnalyzing && (
        <Card className={styles.card}>
          <div style={{ padding: "24px", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Spinner size="medium" className={styles.spinner} />
            <Text align="center" style={{ marginTop: "16px" }}>
              <ArrowSync24Regular />
              Analyzing email content and patterns...
            </Text>
          </div>
        </Card>
      )}

      {showConfirmation && selectedFolder && (
        <Card className={styles.card} style={{ backgroundColor: "#0A75E1", marginTop: "16px" }}>
          <div style={{ padding: "12px", display: "flex", alignItems: "center", gap: "8px" }}>
            <CheckmarkCircle24Regular style={{ color: "white" }} />
            <Text style={{ color: "white" }}>
              Email moved to <strong>{selectedFolder}</strong> folder successfully
            </Text>
          </div>
        </Card>
      )}

      {showResults && (
        <>
          
          <Card className={styles.card}>
            <CardHeader header={<Text weight="semibold">Email Subject</Text>} />
            <CardPreview>
              <div style={{ padding: "12px" }}>
                <Text className={styles.subject}>
                  <DocumentText24Regular />
                  <span>{emailSubject}</span>
                </Text>
              </div>
            </CardPreview>
          </Card>

          <Card className={styles.card}>
            <CardHeader header={<Text weight="semibold">Suggested Folders</Text>} />
            <div style={{ padding: "12px" }}>
              {suggestedFolders.map((folder, index) => (
                <Tooltip
                  key={index}
                  content={`Click to apply '${folder.name}' folder`}
                  relationship="label"
                >
                  <div 
                    className={styles.folderSuggestion}
                    onClick={async () => {
                      // Check security code before proceeding
                      if (validateSecurityCode()) {
                        try {
                          // Call the API to move the email
                          const result = await saveReference(emailId, folder.reference, securityCode);
                          
                          if (result.success) {
                            props.insertText(`Moving to folder: ${folder.name} (Reference: ${folder.reference}) - ${result.message}`);
                            setSelectedFolder(folder.name);
                            setFolderReference(folder.reference); // Set the reference from the selected folder
                            setShowConfirmation(true);
                            setTimeout(() => setShowConfirmation(false), 3000);
                            // Clear security code after successful operation
                            setSecurityCode("");
                          } else {
                            // Show error message from API
                            setErrorMessage(result.message);
                          }
                        } catch (error) {
                          console.error("Error moving email:", error);
                          setErrorMessage("Failed to move email. Please try again.");
                        }
                      }
                    }}
                  >
                    <Text>
                      <span className={styles.folderIcon}>
                        <FolderAdd24Regular />
                      </span>
                      {folder.name}
                      <span style={{ marginLeft: "8px", fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground2 }}>
                        ({folder.reference})
                      </span>
                    </Text>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Badge 
                        appearance={index === 0 ? "filled" : index === 1 ? "outline" : "ghost"}
                        color={index === 0 ? "brand" : index === 1 ? "brand" : "informative"}
                        className={styles.badge}
                      >
                        {Math.round(folder.confidence * 100)}%
                      </Badge>
                      {index === 0 && (
                        <Text size={200} style={{ marginLeft: "8px", color: "#0A75E1" }}>
                          (Recommended)
                        </Text>
                      )}
                    </div>
                  </div>
                </Tooltip>
              ))}
            </div>
          </Card>

          {referenceFound && (
            <Card className={styles.card}>
              <CardHeader 
                header={<Text weight="semibold">Reference Found</Text>} 
                action={
                  <Tooltip content="Reference automatically detected" relationship="label">
                    <CheckmarkCircle24Regular style={{ color: "#0A75E1" }} />
                  </Tooltip>
                }
              />
              <div style={{ padding: "12px" }}>
                <Text>A folder reference was automatically detected in this email.</Text>
                <Text style={{ marginTop: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
                  <DocumentSearch24Regular />
                  <span>Reference: <strong>{detectedReference}</strong></span>
                </Text>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default EmailClassification;